import { Users, Calendar, TrendingUp, ShieldCheck, XCircle } from 'lucide-react';
import { MetricCard, MetricComparison } from './MetricCard';
import { calculateChange } from '@/utils/periodComparison';
import { formatRate } from '@/utils/dateFieldStandards';
import type { CsrCohortStats } from '@/types/reports';

/** Which set of leads a tile stands for; the report turns this into a drill-down. */
export type CsrKpiDrill = 'gradable' | 'booked' | 'book_rate' | 'held' | 'lost_csr';

interface CsrKpiCardsProps {
  totals: CsrCohortStats;
  /** Previous-period totals from useCsrCohortComparison; undefined when compare is off */
  comparison?: { totals: CsrCohortStats; label: string };
  comparisonLoading?: boolean;
  /** Omit to render the tiles as plain (non-clickable) cards. */
  onDrillDown?: (drill: CsrKpiDrill) => void;
}

const HELD_TOOLTIP =
  'Booked jobs that were completed, or cancelled by the tech on site (cancellation reason starting "Tech Cancelled"), ' +
  'as a share of booked jobs that are completed or cancelled. Jobs still scheduled, in progress, on hold or quote-sent are not counted yet.';

const LEADS_TOOLTIP =
  'Leads created in the range, minus leads that were never a real opportunity (wrong number, spam, out of scope, existing customer…) ' +
  'and leads lost because no tech was available. This is the denominator for every rate on the page.';

export function CsrKpiCards({ totals, comparison, comparisonLoading = false, onDrillDown }: CsrKpiCardsProps) {
  const drill = (key: CsrKpiDrill) => (onDrillDown ? () => onDrillDown(key) : undefined);

  const compare = (current: number, previous: number | undefined, isPositiveGood = true): MetricComparison | undefined => {
    if (!comparison || previous === undefined) return undefined;
    return { ...calculateChange(current, previous), label: comparison.label, isPositiveGood };
  };

  const compareRate = (current: number | null, previous: number | null | undefined, isPositiveGood = true): MetricComparison | undefined => {
    if (!comparison || current === null || previous === null || previous === undefined) return undefined;
    return { ...calculateChange(current, previous), label: comparison.label, isPositiveGood };
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      <MetricCard
        title="Gradable leads"
        value={totals.leads.toString()}
        subtitle={`${totals.cohort} received · ${totals.excluded} excluded · ${totals.lost_ops} lost to ops`}
        tooltip={LEADS_TOOLTIP}
        icon={Users}
        valueColor="text-info"
        iconBgColor="bg-info/10"
        iconColor="text-info"
        onClick={drill('gradable')}
        comparison={compare(totals.leads, comparison?.totals.leads)}
        comparisonLoading={comparisonLoading}
      />
      <MetricCard
        title="Booked"
        value={totals.booked.toString()}
        icon={Calendar}
        valueColor="text-success"
        iconBgColor="bg-success/10"
        iconColor="text-success"
        onClick={drill('booked')}
        comparison={compare(totals.booked, comparison?.totals.booked)}
        comparisonLoading={comparisonLoading}
      />
      <MetricCard
        title="Book %"
        value={formatRate(totals.book_rate)}
        subtitle="Booked ÷ gradable leads"
        icon={TrendingUp}
        onClick={drill('book_rate')}
        comparison={compareRate(totals.book_rate, comparison?.totals.book_rate)}
        comparisonLoading={comparisonLoading}
      />
      <MetricCard
        title="Held %"
        value={formatRate(totals.held_rate)}
        subtitle={`${totals.held_numerator} of ${totals.held_denominator} decided jobs`}
        tooltip={HELD_TOOLTIP}
        icon={ShieldCheck}
        onClick={drill('held')}
        comparison={compareRate(totals.held_rate, comparison?.totals.held_rate)}
        comparisonLoading={comparisonLoading}
      />
      <MetricCard
        title="Lost by CSR"
        value={totals.lost_csr.toString()}
        subtitle="Customer not ready, price, went elsewhere…"
        icon={XCircle}
        valueColor="text-destructive"
        iconBgColor="bg-destructive/10"
        iconColor="text-destructive"
        onClick={drill('lost_csr')}
        comparison={compare(totals.lost_csr, comparison?.totals.lost_csr, false)}
        comparisonLoading={comparisonLoading}
      />
    </div>
  );
}
